import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  fetchGameDetails,
  fetchGameScreenshots,
  fetchGameMovies,
  fetchGameReviews
} from "../services/api";
import { FaStar, FaExternalLinkAlt } from "react-icons/fa";
import AddReviewForm from "../components/AddReviewForm";
import "../styles/GameDetail.css";

const TABS = [
  "Overview",
  "Trailers",
  "Screenshots",
  "Requirements",
  "Reviews"
];

const getYouTubeSearchUrl = (gameName) =>
  `https://www.youtube.com/results?search_query=${encodeURIComponent(gameName + " official trailer")}`;

const GameDetail = () => {
  const { id } = useParams();
  const [game, setGame] = useState(null);
  const [tab, setTab] = useState(TABS[0]);
  const [screenshots, setScreenshots] = useState([]);
  const [movies, setMovies] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [fetchError, setFetchError] = useState("");


  useEffect(() => {
    fetchGameDetails(id).then(setGame);
    fetchGameScreenshots(id).then(setScreenshots);
    fetchGameMovies(id).then(setMovies);
    fetchGameReviews(id)
      .then(setReviews)
      .catch(() => setFetchError("Could not load reviews right now."));
  }, [id]);

  if (!game) return <div className="game-detail-wide">Loading...</div>;

  const trailerVideo =
    movies.length > 0 && movies[0].data?.max ? movies[0].data.max : null;
  const trailerYouTube =
    movies.length > 0 && movies[0].data?.youtube_id
      ? `https://www.youtube.com/embed/${movies[0].data?.youtube_id}`
      : null;

  const pcPlatform = game.platforms
    ? game.platforms.find(
        (p) =>
          p.platform.name === "PC" &&
          (p.requirements || p.requirements_en)
      )
    : null;
  const minReq =
    pcPlatform?.requirements?.minimum ||
    pcPlatform?.requirements_en?.minimum ||
    "";
  const recReq =
    pcPlatform?.requirements?.recommended ||
    pcPlatform?.requirements_en?.recommended ||
    "";



  return (
    <div className="game-detail-wide" data-page="game-detail" style={{ textAlign: "center" }}>
      <h1 className="game-detail-top-title">{game.name}</h1>
      <img
        src={game.background_image}
        alt={game.name}
        className="game-detail-top-cover"
        style={{
          borderRadius: "20px",
          boxShadow: "0 6px 28px rgba(0,0,0,0.23)",
          width: "700px",
          maxWidth: "97vw",
         
          height: "auto",
          display: "block",
          margin: "18px auto 10px auto"
        }}
      />
      <div
        className="game-detail-tabs"
        style={{
          display: "flex",
          justifyContent: "center",
          margin: "20px 0",
          gap: "10px"
        }}
      >
        {TABS.map((tabName) => (
          <button
            key={tabName}
            className={tab === tabName ? "active" : ""}
            style={{
              padding: "9px 25px",
              borderRadius: "14px",
              background: tab === tabName ? "#7841d7" : "#23233a",
              color: "#fff",
              fontWeight: "bold",
              border: "none",
              cursor: "pointer",
              fontSize: "1.12rem"
            }}
            onClick={() => setTab(tabName)}
          >
            {tabName}
          </button>
        ))}
      </div>
      <div style={{ margin: "0 auto", maxWidth: "760px" }}>
        {tab === "Overview" && (
          <section className="game-detail-sec" style={{
            margin: "25px auto",
            background: "#23233a",
            borderRadius: "18px",
            padding: "22px 30px"
          }}>
            <h2>About</h2>
            <p className="game-detail-desc">
              {game.description_raw || game.description || "Check back soon for more details."}
            </p>
            <div className="game-detail-info-badges">
              <span><FaStar color="#FFD700" /> {Math.round(game.rating)} / 5</span>
              <span>Released: <b>{game.released}</b></span>
              {game.genres?.map(g => <span className="game-chip" key={g.id}>{g.name}</span>)}
              {game.platforms?.map(p => <span className="game-chip game-chip-outline" key={p.platform.id}>{p.platform.name}</span>)}
              {game.tags?.slice(0, 5).map(tag => <span className="game-chip game-chip-tag" key={tag.id}>{tag.name}</span>)}
              {game.stores?.[0]?.store?.name && (
                <span className="game-detail-store">{game.stores[0].store.name}</span>
              )}
              {game.website && (
                <a href={game.website} target="_blank" rel="noopener noreferrer" className="game-detail-link">
                  Official Website <FaExternalLinkAlt />
                </a>
              )}
            </div>
          </section>
        )}
        {tab === "Trailers" && (
          <section className="game-detail-sec" style={{
            margin: "25px auto",
            background: "#23233a",
            borderRadius: "18px",
            padding: "22px 30px"
          }}>
            <h2>Trailer</h2>
            {(() => {
              // Prefer RAWG movie source; then RAWG game.clip; then YouTube ID; then search embed
              const first = movies && movies.length ? movies[0] : null;
              const direct = first?.data?.max || first?.data?.['480'] || null;
              const clip = game?.clip?.clip || null;
              const ytId = first?.data?.youtube_id || null;
              const ytSrc = ytId ? `https://www.youtube-nocookie.com/embed/${ytId}?modestbranding=1&rel=0` : null;
              const searchSrc = `https://www.youtube-nocookie.com/embed?modestbranding=1&rel=0&listType=search&list=${encodeURIComponent(game.name + ' official trailer')}`;

              if (direct) {
                return (
                  <video controls src={direct} className="game-detail-video" style={{ width: 700, maxWidth: '100%', borderRadius: 12 }}>
                    Your browser does not support video.
                  </video>
                );
              }
              if (clip) {
                return (
                  <video controls src={clip} className="game-detail-video" style={{ width: 700, maxWidth: '100%', borderRadius: 12 }}>
                    Your browser does not support video.
                  </video>
                );
              }
              return (
                <iframe
                  width="700"
                  height="394"
                  src={ytSrc || searchSrc}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  title={`${game.name} Trailer`}
                  className="game-detail-video"
                  style={{ width: '100%', borderRadius: 12 }}
                />
              );
            })()}
          </section>
        )}
        {tab === "Screenshots" && (
          <section className="game-detail-sec" style={{
            margin: "25px auto",
            background: "#23233a",
            borderRadius: "18px",
            padding: "22px 30px"
          }}>
            <h2>Screenshots</h2>
            {screenshots.length > 0 ? (
              screenshots.map(img => (
                <img
                  key={img.id}
                  src={img.image}
                  alt="Game Screenshot"
                  className="game-detail-shot"
                  style={{
                    width: "700px",
                    maxWidth: "97vw",
                    borderRadius: "15px",
                    display: "block",
                    margin: "0 auto 22px auto",
                    boxShadow: "0 2px 18px rgba(50,50,90,0.18)"
                  }}
                />
              ))
            ) : (
              <div className="game-detail-media-fallback">No screenshots available.</div>
            )}
          </section>
        )}
        {tab === "Requirements" && (
          <section className="game-detail-sec" style={{
            margin: "25px auto",
            background: "#23233a",
            borderRadius: "18px",
            padding: "22px 30px"
          }}>
            <h2>PC Requirements</h2>
            {minReq || recReq ? (
              <div className="game-detail-requirements">
                {minReq && (
                  <div>
                    <strong>Minimum:</strong>
                    <div className="game-detail-req">{minReq}</div>
                  </div>
                )}
                {recReq && (
                  <div>
                    <strong>Recommended:</strong>
                    <div className="game-detail-req">{recReq}</div>
                  </div>
                )}
              </div>
            ) : (
              <div className="game-detail-media-fallback">No requirements available.</div>
            )}
          </section>
        )}
        {tab === "Reviews" && (
          <section className="game-detail-sec" style={{
            margin: "25px auto",
            background: "#23233a",
            borderRadius: "18px",
            padding: "22px 30px"
          }}>
                         <h2>User Reviews</h2>
             <AddReviewForm 
               gameId={id} 
               onReviewAdded={() => {
                 fetchGameReviews(id)
                   .then(setReviews)
                   .catch(() => setFetchError("Could not reload reviews after submit."));
               }}
             />
            <div className="review-list">
              {fetchError ? (
                <div style={{
                  background: "#31316422",
                  color: "#ff5860",
                  borderRadius: "7px",
                  padding: "14px 18px",
                  textAlign: "center"
                }}>
                  {fetchError}
                </div>
              ) : reviews && reviews.length ? (
                reviews.map(r => (
                  <div key={r.id || r.createdAt} style={{
                    background: "#181820",
                    color: "#dde1e5",
                    padding: "14px 18px",
                    borderRadius: "9px",
                    marginBottom: "13px",
                    textAlign: "left"
                  }}>
                    <div style={{ fontWeight: "bold", marginBottom: "5px" }}>
                      {r.userName ? r.userName : "Anonymous"}
                    </div>
                    <div style={{ color: "#ffd700", marginBottom: "6px" }}>★ {r.rating}/5</div>
                    <div style={{ marginBottom: "3px" }}>{r.text}</div>
                    <div style={{ fontSize: "0.97rem", color: "#aad" }}>
                      {r.createdAt ? new Date(r.createdAt).toLocaleString() : ""}
                    </div>
                  </div>
                ))
              ) : (
                <div style={{
                  background: "#31316422",
                  color: "#aad",
                  borderRadius: "7px",
                  padding: "14px 18px",
                  textAlign: "center"
                }}>
                  No reviews yet.
                </div>
              )}
            </div>
          </section>
        )}
      </div>
      <div className="game-detail-back" style={{ marginTop: "35px" }}>
        <Link to="/">← Back</Link>
      </div>
    </div>
  );
};

export default GameDetail;
